import { useState, useEffect } from 'react';
import { useCalculatorStore } from '../../store/calculatorStore';
import { ScenarioPresets } from '../shared/ScenarioPresets';
import { DiscountModifier } from '../shared/DiscountModifier';
import { useLogicAppsCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { FeedbackModal } from '../shared/FeedbackModal';
import { ResourcesSection } from '../shared/ResourcesSection';
import { ShareButton } from '../shared/ShareButton';
import { CurrencyTooltip } from '../shared/CurrencyTooltip';
import { FormulaDisclosure } from '../shared/FormulaDisclosure';
import { FieldTooltip } from '../shared/FieldTooltip';

// LogicAppsCalc.tsx
// Converted from Stitch screen: desktop_logic_apps_view/code.html

export default function LogicAppsCalc() {
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { logicAppsInputs, setLogicAppsInputs, discount, setDiscount } = useCalculatorStore();
  const { data: calculation, isLoading, isError } = useLogicAppsCalc(logicAppsInputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (calculation?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'logic-apps', result_gbp: calculation.monthly_estimate, tier: logicAppsInputs.planType })
    }
  }, [calculation?.monthly_estimate])

  const handlePlanTypeChange = (planType: 'consumption' | 'standard') => {
    setLogicAppsInputs({ planType });
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const runsPerDay = parseInt(e.target.value);
    setLogicAppsInputs({ runsPerDay });
  };

  const handleInputChange = (field: keyof typeof logicAppsInputs, value: string) => {
    const val = parseInt(value) || 0;
    setLogicAppsInputs({ [field]: val });
  };

  return (
    <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-opacity ${isLoading ? 'opacity-70' : ''}`}>
      {/* Left Column: Configuration (65%) */}
      <div className="lg:col-span-8 flex flex-col gap-6">
        <ScenarioPresets presets={[
          {
            label: 'Simple automation',
            description: 'Consumption · 100 runs/day · 5 actions · standard connectors',
            apply: () => setLogicAppsInputs({ planType: 'consumption', runsPerDay: 100, actionsPerRun: 5, standardConnectorCallsPerMonth: 15000, enterpriseConnectorCallsPerMonth: 0 }),
          },
          {
            label: 'Event-driven integration',
            description: 'Consumption · 1,000 runs/day · 10 actions — mid-volume pattern',
            apply: () => setLogicAppsInputs({ planType: 'consumption', runsPerDay: 1000, actionsPerRun: 10, standardConnectorCallsPerMonth: 300000, enterpriseConnectorCallsPerMonth: 0 }),
          },
          {
            label: 'Enterprise SAP integration',
            description: 'Standard plan · 500 runs/day · enterprise connectors (SAP/Salesforce)',
            apply: () => setLogicAppsInputs({ planType: 'standard', runsPerDay: 500, actionsPerRun: 20, standardConnectorCallsPerMonth: 0, enterpriseConnectorCallsPerMonth: 300000 }),
          },
        ]} />
        {/* Plan Type Selector */}
        <div className="bg-surface-container-high p-6 border border-outline-variant/40 rounded-sm">
          <h2 className="text-[11px] font-mono uppercase tracking-[0.2em] text-on-surface-variant mb-4">Plan Type Selection</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Consumption Selection */}
            <div 
              onClick={() => handlePlanTypeChange('consumption')}
              className={`relative p-4 cursor-pointer transition-all ${logicAppsInputs.planType === 'consumption' ? 'bg-primary-container/10 border-2 border-primary-container' : 'bg-surface-container-low border border-outline-variant/40 hover:border-outline-variant'}`}
            >
              <div className="flex justify-between items-start mb-2">
                {logicAppsInputs.planType === 'consumption' && (
                  <span className="font-headline font-semibold text-on-primary-container bg-primary-container px-2 py-0.5 text-[10px] rounded-sm">ACTIVE</span>
                )}
                <span className={`material-symbols-outlined ${logicAppsInputs.planType === 'consumption' ? 'text-primary' : 'text-outline-variant'}`}>offline_bolt</span>
              </div>
              <h3 className="font-headline text-lg font-bold text-on-surface">Consumption</h3>
              <p className="text-xs text-on-surface-variant mt-1 leading-relaxed">Pay-per-execution. Best for unpredictable traffic and serverless scaling.</p>
            </div>
            {/* Standard Selection */}
            <div 
              onClick={() => handlePlanTypeChange('standard')}
              className={`relative p-4 cursor-pointer transition-all ${logicAppsInputs.planType === 'standard' ? 'bg-primary-container/10 border-2 border-primary-container' : 'bg-surface-container-low border border-outline-variant/40 hover:border-outline-variant'}`}
            >
              <div className="flex justify-between items-start mb-2">
                {logicAppsInputs.planType === 'standard' && (
                  <span className="font-headline font-semibold text-on-primary-container bg-primary-container px-2 py-0.5 text-[10px] rounded-sm">ACTIVE</span>
                )}
                <span className={`material-symbols-outlined ${logicAppsInputs.planType === 'standard' ? 'text-primary' : 'text-outline-variant'}`}>hub</span>
              </div>
              <h3 className="font-headline text-lg font-bold text-on-surface">Standard</h3>
              <p className="text-xs text-on-surface-variant mt-1 leading-relaxed">Dedicated compute. Best for high-throughput, VNET, and predictable billing.</p>
            </div>
          </div>
        </div>

        {/* Inputs Form */}
        <div className="bg-surface-container-high p-6 border border-outline-variant/40 rounded-sm flex flex-col gap-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Throughput Slider */}
            <div className="flex flex-col gap-4">
              <div className="flex justify-between items-end">
                <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-on-surface-variant">Runs per Day</label>
                <span className="font-mono text-primary text-sm font-bold">{logicAppsInputs.runsPerDay?.toLocaleString()}</span>
              </div>
              <input 
                className="w-full h-1 bg-surface-container-lowest rounded-lg appearance-none cursor-pointer accent-primary-container" 
                max="10000" 
                min="0" 
                step="100"
                type="range" 
                value={logicAppsInputs.runsPerDay || 0}
                onChange={handleSliderChange}
              />
              <div className="flex justify-between text-[10px] font-mono text-outline">
                <span>0</span>
                <span>10k</span>
              </div>
            </div>
            {/* Actions Input */}
            <div className="flex flex-col gap-4">
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-on-surface-variant">Actions per Run</label>
              <div className="relative">
                <input 
                  className="w-full bg-surface-container-lowest border border-outline-variant/60 p-3 text-sm text-on-surface focus:border-primary-container outline-none font-mono" 
                  type="number" 
                  value={logicAppsInputs.actionsPerRun || ''}
                  onChange={(e) => handleInputChange('actionsPerRun', e.target.value)}
                  placeholder="5"
                />
                <span className="absolute right-3 top-3 text-[10px] text-outline-variant font-mono uppercase">Ops / Exec</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Standard Connectors */}
            <div className="flex flex-col gap-4">
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-on-surface-variant">Standard Connectors <FieldTooltip content="Pre-built connectors for common services like Office 365, SharePoint, and Dropbox. Lower per-call cost than Enterprise." /></label>
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <input 
                    className="w-full bg-surface-container-lowest border border-outline-variant/60 p-3 text-sm text-on-surface focus:border-primary-container outline-none font-mono" 
                    value={logicAppsInputs.standardConnectorCallsPerMonth || ''}
                    onChange={(e) => handleInputChange('standardConnectorCallsPerMonth', e.target.value)}
                    placeholder="0" 
                    type="number"
                  />
                </div>
                <div className="text-xs text-on-surface-variant w-24 leading-tight italic">SaaS, Azure, & Cloud</div>
              </div>
            </div>
            {/* Enterprise Connectors */}
            <div className="flex flex-col gap-4">
              <label className="text-[11px] font-mono uppercase tracking-[0.15em] text-on-surface-variant">Enterprise Connectors <FieldTooltip content="Connectors for SAP, Salesforce, and other enterprise systems. Higher per-call cost than Standard connectors." /></label>
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <input 
                    className="w-full bg-surface-container-lowest border border-outline-variant/60 p-3 text-sm text-on-surface focus:border-primary-container outline-none font-mono" 
                    value={logicAppsInputs.enterpriseConnectorCallsPerMonth || ''}
                    onChange={(e) => handleInputChange('enterpriseConnectorCallsPerMonth', e.target.value)}
                    placeholder="0" 
                    type="number"
                  />
                </div>
                <div className="text-xs text-on-surface-variant w-24 leading-tight italic">SAP, Oracle, IBM</div>
              </div>
            </div>
          </div>
          {/* Action Execution Note */}
          <div className="bg-[#e8b000]/10 border-l-4 border-[#e8b000] p-4 flex gap-4">
            <span className="material-symbols-outlined text-[#ffb900]">warning</span>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold text-[#ffb900] uppercase tracking-wider">Action Execution Note</span>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                Every step in your workflow is counted as an action, including control-flow steps (loops, conditions, variables). A typical complex workflow averages 15+ actions per execution.
              </p>
            </div>
          </div>
        </div>

        {/* Bento Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-surface-container-high p-4 border border-outline-variant/20 rounded-sm">
            <h4 className="text-[10px] font-mono text-outline mb-2 uppercase">Region</h4>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">UK South</span>
              <span className="material-symbols-outlined text-xs text-outline">public</span>
            </div>
          </div>
          <div className="bg-surface-container-high p-4 border border-outline-variant/20 rounded-sm">
            <h4 className="text-[10px] font-mono text-outline mb-2 uppercase">Currency</h4>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">GBP (£)</span>
              <span className="material-symbols-outlined text-xs text-outline">payments</span>
            </div>
          </div>
          <div className="bg-surface-container-high p-4 border border-outline-variant/20 rounded-sm">
            <h4 className="text-[10px] font-mono text-outline mb-2 uppercase">Support</h4>
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">Standard</span>
              <span className="material-symbols-outlined text-xs text-outline">support_agent</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Results (35%) */}
      <div className="lg:col-span-4 flex flex-col gap-6 sticky top-[80px]">
        <div className="bg-surface-container-high border border-outline-variant/40 rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-outline-variant/40 flex justify-between items-center" style={{ backgroundColor: 'var(--surface-container-high)' }}>
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-on-surface">Monthly Estimate (PAYG)</span>
            <span className="material-symbols-outlined text-primary text-sm">insights</span>
          </div>
          <div className="p-8 flex flex-col gap-8">
            {/* Main Price */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-headline font-extrabold text-on-surface tracking-tighter">
                  {isLoading ? '...' : isError ? 'Error' : `£${calculation?.monthly_estimate.toFixed(2)}`}
                </span>
                {!isLoading && !isError && calculation && <CurrencyTooltip />}
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  title="Report an issue with this estimate"
                  className="transition-colors"
                  style={{ color: 'var(--text-tertiary)' }}
                >
                  <span className="material-symbols-outlined text-[18px]">help</span>
                </button>
              </div>
              <div className="text-[10px] font-mono text-outline-variant uppercase">Estimated Total / Month</div>
            </div>

            {/* Price Breakdown */}
            <div className="flex flex-col gap-4">
              {calculation?.breakdown.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center pb-2 border-b border-outline-variant/20">
                  <span className="text-xs text-on-surface-variant">{item.label}</span>
                  <span className="text-xs font-mono text-on-surface">£{item.value.toFixed(2)}</span>
                </div>
              ))}
              {!calculation && !isLoading && (
                <p className="text-[10px] text-outline text-center py-4">Adjust configuration to see pricing</p>
              )}
            </div>

            {/* Disclaimer / Free Tier Note */}
            <div className="bg-surface-container-lowest p-3 border border-outline-variant/40 rounded-sm">
              <div className="flex gap-2 items-center mb-1">
                <span className="material-symbols-outlined text-[14px] text-primary">auto_awesome</span>
                <span className="text-[10px] font-bold text-on-surface uppercase tracking-wider">Pricing Note</span>
              </div>
              <p className="text-[10px] text-on-surface-variant leading-tight">
                {calculation?.disclaimer || "First 4,000 actions are free each month in this subscription."}
              </p>
            </div>

            {calculation && (
              <FormulaDisclosure
                lines={[
                  ...calculation.breakdown.map((item) => `${item.label}: £${item.value.toFixed(4)}`),
                  `Plan: ${logicAppsInputs.planType.charAt(0).toUpperCase() + logicAppsInputs.planType.slice(1)} · Region: UK South`,
                ]}
                fetchedAt={calculation.prices_last_updated}
              />
            )}
            <button className="w-full bg-[#0078d4] hover:bg-[#0060ab] text-white font-headline text-xs font-bold py-3 transition-all flex items-center justify-center gap-2 uppercase tracking-widest">
              <span className="material-symbols-outlined text-sm">shopping_cart_checkout</span>
              Add to Project
            </button>
          </div>
        </div>

        {/* Secondary Result Card */}
        <div className="bg-surface-container-high p-6 border border-outline-variant/40 rounded-sm">
          <h4 className="text-[10px] font-mono text-outline mb-4 uppercase tracking-[0.1em]">Cost Optimization AI</h4>
          <p className="text-xs text-on-surface-variant leading-relaxed">
            Based on your current configuration, we're ensuring you're utilizing the most cost-effective tier for UK South.
          </p>
        </div>
        {calculation && (
          <DiscountModifier
            discount={discount}
            onDiscountChange={setDiscount}
            paygTotal={calculation.monthly_estimate}
          />
        )}
        <div className="px-4 pb-2">
          <ShareButton inputs={logicAppsInputs} result={calculation ?? null} service="logic-apps" />
        </div>
        <ResourcesSection service="logic-apps" />
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="logic-apps"
          estimateValue={calculation ? `£${calculation.monthly_estimate.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}
    </div>
  );
}
